export interface InsightPreview {
  summary: string;
  takeaways: string[];
}

interface MarkdownSection {
  lines: string[];
  level: number | null;
}

function cleanText(value: string): string {
  return value
    .replace(/^\s*(?:#{1,6}\s+|[-*>]\s+|\d+[.)]\s+)/, '')
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function firstSubstantiveLine(lines: string[]): string {
  return lines
    .map(cleanText)
    .find((line) => line.length > 20 && line !== '---') ?? '';
}

function isHeading(line: string): boolean {
  return /^#{1,6}\s+/.test(line);
}

function headingLevel(line: string): number | null {
  const match = line.match(/^(#{1,6})\s+/);
  return match ? match[1].length : null;
}

function isListItem(line: string): boolean {
  return /^\s*(?:[-*>]\s+|\d+[.)]\s+)/.test(line);
}

function firstPreviewSentence(lines: string[]): string {
  const prose = lines
    .filter((line) => !isHeading(line) && !isListItem(line))
    .map(cleanText)
    .find((line) => line.length > 20 && !line.endsWith(':') && /[.!?][”')\]]?$/.test(line));

  return prose || firstSubstantiveLine(lines.filter((line) => !isHeading(line)));
}

function section(lines: string[], headingPattern: RegExp): MarkdownSection {
  const start = lines.findIndex((line) => isHeading(line) && headingPattern.test(line));
  if (start < 0) return { lines: [], level: null };

  const level = headingLevel(lines[start]);
  const rest = lines.slice(start + 1);
  const end = rest.findIndex((line) => {
    const nextLevel = headingLevel(line);
    return nextLevel !== null && level !== null && nextLevel <= level;
  });
  return { lines: end < 0 ? rest : rest.slice(0, end), level };
}

export function extractInsightPreview(content: string): InsightPreview {
  const lines = content.split('\n');
  const summarySection = section(
    lines,
    /paper in one sentence|paper at a glance|executive summary|overview/i,
  );
  const summary = firstPreviewSentence(summarySection.lines) || firstPreviewSentence(lines);

  const contributions = section(lines, /key contributions|takeaways/i);
  const contributionLevel = contributions.level;
  const subsectionLevel = contributionLevel === null
    ? null
    : Math.min(
      ...contributions.lines
        .map(headingLevel)
        .filter((level): level is number => level !== null && level > contributionLevel),
    );
  const subsectionStarts = contributions.lines
    .map((line, index) => headingLevel(line) === subsectionLevel ? index : -1)
    .filter((index) => index >= 0);
  const takeaways = subsectionStarts.length > 0
    ? subsectionStarts
      .map((start, index) => firstPreviewSentence(
        contributions.lines.slice(start + 1, subsectionStarts[index + 1]),
      ))
      .filter(Boolean)
      .slice(0, 3)
    : contributions.lines
      .filter((line) => !isHeading(line))
      .map(cleanText)
      .filter((line) => line.length > 20)
      .slice(0, 3);

  return { summary, takeaways };
}
